import { computed, inject, Injectable, linkedSignal, Signal, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { dsDtos, dsEnums } from '@vectronic/openApi-admin';
import {
  ParameterDataService,
  ParameterFormatService,
  ScheduleHelp,
  ScheduleResponseModel,
} from '@vectronic/util';
import {
  TelemetryCompareEntry,
  TelemetryForComparison,
} from '../models/telemetry-for-comparison.model';

@Injectable()
export class TelemetryComparisonService {
  private readonly parameterFormatService = inject(ParameterFormatService);
  private readonly parameterDataService = inject(ParameterDataService);

  private collarsSource: Signal<TelemetryForComparison[]> = signal<TelemetryForComparison[]>([]);
  private parametersSource: Signal<dsDtos.Parameter[]> = signal<dsDtos.Parameter[]>([]);
  private parameterCategoriesSource: Signal<dsDtos.ParameterCategory[]> = signal<
    dsDtos.ParameterCategory[]
  >([]);
  private parameterSubsystemsSource: Signal<dsDtos.Subsystem[]> = signal<dsDtos.Subsystem[]>([]);

  readonly collars = linkedSignal({
    source: () => this.collarsSource(),
    computation: (collars) => collars,
  });
  readonly parameters = linkedSignal({
    source: () => this.parametersSource(),
    computation: (parameters) => parameters,
  });
  readonly parameterCategories = linkedSignal({
    source: () => this.parameterCategoriesSource(),
    computation: (categories) => categories,
  });
  readonly parameterSubsystems = linkedSignal({
    source: () => this.parameterSubsystemsSource(),
    computation: (subsystems) => subsystems,
  });

  setDataSources(sources: {
    collars: Signal<TelemetryForComparison[]>;
    parameters: Signal<dsDtos.Parameter[]>;
    parameterCategories: Signal<dsDtos.ParameterCategory[]>;
    parameterSubsystems: Signal<dsDtos.Subsystem[]>;
  }): void {
    this.collarsSource = sources.collars;
    this.parametersSource = sources.parameters;
    this.parameterCategoriesSource = sources.parameterCategories;
    this.parameterSubsystemsSource = sources.parameterSubsystems;
  }

  readonly selectedCollarIds = computed(() => this.collars().map((c) => c.id));

  readonly isGsmSelected = computed(() => this.collars()[0]?.comType === 'GSM');

  readonly permanentEndDate = computed(() => (this.isGsmSelected() ? '2038-01-01' : '2050-01-01'));

  private readonly scheduleTypeLabels: Partial<
    Record<dsEnums.ScheduleType | 'VIRTUAL_FENCE_MAP', string>
  > = {
    GPS: 'GPS',
    VHF: 'VHF',
    COMMUNICATION: 'Communication',
    PROXIMITY: 'Proximity',
    VIRTUAL_FENCE: 'Virtual Fence',
    VIRTUAL_FENCE_MAP: 'Virtual Fence Map',
    CAMERA: 'Camera',
    HIGH_ACTIVITY_GPS: 'High Activity GPS',
    SOLEX_GPS: 'Solex GPS',
    EXTERNAL_SENSOR_RECEIVER: 'External Sensor Receiver',
  };

  readonly multiDeviceValuesByScheduleType = computed(() => {
    const collars = this.collars();
    const result = new Map<dsEnums.ScheduleType | 'VIRTUAL_FENCE_MAP', string>();

    if (collars.length < 2) return result;

    const allScheduleTypes = new Set<dsEnums.ScheduleType>();
    for (const collar of collars) {
      for (const s of collar.schedules) {
        if (s.scheduleType) allScheduleTypes.add(s.scheduleType as dsEnums.ScheduleType);
      }
    }

    for (const scheduleType of allScheduleTypes) {
      const deviceSchedules = collars.map((c) => {
        const matching = c.schedules.filter((s) => s.scheduleType === scheduleType);
        return matching.find((s) => s.userDefined) ?? matching[0];
      });
      const first = deviceSchedules[0];
      const allSame = deviceSchedules.every((s) => ScheduleHelp.areSchedulesEqual(s, first));
      if (!allSame) {
        const label = this.scheduleTypeLabels[scheduleType] ?? scheduleType;
        result.set(scheduleType, `Selected devices have different ${label} schedules.`);
      }
    }

    const virtualFences = collars.map((c) => JSON.stringify(c.virtualFenceConstructionRules));
    if (new Set(virtualFences).size > 1) {
      result.set(
        'VIRTUAL_FENCE_MAP',
        'Selected devices have different Virtual Fence Map schedules.',
      );
    }

    return result;
  });

  readonly hideConstructionRules = computed(() => {
    const collars = this.collars();
    if (collars.length < 2) return false;
    const virtualFences = collars.map((c) => JSON.stringify(c.virtualFenceConstructionRules));
    return new Set(virtualFences).size > 1;
  });

  readonly hideRulesByScheduleType = computed(() => {
    const result = new Map<dsEnums.ScheduleType | 'VIRTUAL_FENCE_MAP', boolean>();
    for (const key of this.multiDeviceValuesByScheduleType().keys()) {
      result.set(key, true);
    }
    return result;
  });

  readonly scheduleResponse = computed<ScheduleResponseModel>(() => {
    const scheduleResponse: ScheduleResponseModel = {};
    for (const collar of this.collars()) {
      const scheduleTypes = new Set<string>();
      for (const schedule of collar.schedules) {
        if (schedule.scheduleType) scheduleTypes.add(schedule.scheduleType);
      }
      for (const scheduleType of scheduleTypes) {
        if (!scheduleResponse[scheduleType]) scheduleResponse[scheduleType] = [];
        (scheduleResponse[scheduleType] as number[]).push(collar.id);
      }
    }
    return scheduleResponse;
  });

  readonly forceShowScheduleTypes = computed<(dsEnums.ScheduleType | 'VIRTUAL_FENCE_MAP')[]>(() => {
    const response = this.scheduleResponse();
    const collarIds = this.selectedCollarIds();
    const types: dsEnums.ScheduleType[] = [
      'GPS',
      'VHF',
      'CAMERA',
      'COMMUNICATION',
      'VIRTUAL_FENCE',
      'PROXIMITY',
      'SOLEX_GPS',
    ];
    return types.filter((type) => response[type]?.some((id) => collarIds.includes(id)));
  });

  readonly telemetryCategoryTitles = computed(() => {
    const titles: { label: string; value: dsEnums.ScheduleType | 'VIRTUAL_FENCE_MAP' }[] = [
      { label: 'Schedules', value: 'GPS' },
    ];
    const hasVirtualFenceMap = this.collars().some((c) => c.virtualFenceConstructionRules.length);
    if (hasVirtualFenceMap) {
      titles.push({ label: 'Virtual Fence Map', value: 'VIRTUAL_FENCE_MAP' });
    }
    return titles;
  });

  readonly comparableAvailableParamIds = computed(() => {
    const collars = this.collars();
    if (!collars.length) return new Set<string>();

    return new Set(
      collars.flatMap(
        (c) => c.availableParameterConfigs.map((p) => p?.parameterId).filter(Boolean) as string[],
      ),
    );
  });

  readonly availableParameters = computed(() => {
    const parameters = this.parameters();
    const comparableIds = this.comparableAvailableParamIds();
    if (!parameters.length || !comparableIds.size) return [];
    return parameters.filter(({ id }) => comparableIds.has(id));
  });

  readonly deviceTypeParameterConfigWithAvailableList = computed(() => {
    const result: (dsDtos.DeviceTypeParameterConfig & { availableForDevices: number[] })[] = [];
    for (const collar of this.collars()) {
      for (const paramConfig of collar.availableParameterConfigs) {
        const existing = result.find((c) => c.parameterId === paramConfig.parameterId);
        if (existing) {
          if (!existing.availableForDevices.includes(collar.id)) {
            existing.availableForDevices.push(collar.id);
          }
        } else {
          result.push({ ...paramConfig, availableForDevices: [collar.id] });
        }
      }
    }
    return result;
  });

  readonly showMode = signal<'differences' | 'all'>('differences');

  readonly userOrFactory = signal<'customer' | 'all'>('customer');

  readonly telemetryCompareData = computed<TelemetryCompareEntry[]>(() =>
    this.collars().map((c) => ({
      id: c.id,
      label: c.label,
      schedules: c.schedules,
    })),
  );

  /** Map<collarId (string), Map<parameterId, formattedValue>> for all available parameters. */
  private readonly perCollarValues = computed(() => {
    const collars = this.collars();
    const parameters = this.availableParameters();
    const result = new Map<string, Map<string, string>>();

    for (const collar of collars) {
      const collarKey = String(collar.id);
      const paramMap = new Map<string, string>();

      for (const parameter of parameters) {
        const userData = collar.parameterDataList.find(
          (pd) => pd.parameterId === parameter.id && pd.isUserData,
        );
        const factoryData = collar.parameterDataList.find(
          (pd) => pd.parameterId === parameter.id && !pd.isUserData,
        );
        const paramEntry = (userData?.data != null ? userData : undefined) ?? factoryData;
        const raw = paramEntry?.data;

        let displayValue: string;
        if (raw === undefined || raw === null || raw === '') {
          displayValue = '—';
        } else {
          displayValue = this.parameterFormatService.getFormattedData(paramEntry, parameter) || raw;
        }

        paramMap.set(parameter.id, displayValue);
      }

      result.set(collarKey, paramMap);
    }

    return result;
  });

  /** Set of parameter IDs where at least two connected devices have different values. */
  readonly differingParameterIds = computed(() => {
    const collars = this.collars();
    const parameters = this.availableParameters();
    const result = new Set<string>();

    if (collars.length < 2) return result;

    const collarMaps = Array.from(this.perCollarValues().values());

    for (const parameter of parameters) {
      const values = collarMaps.map((m) => m.get(parameter.id));
      const allSame = values.every((v) => v === values[0]);
      if (!allSame) result.add(parameter.id);
    }

    return result;
  });

  /**
   * Mirrors ParameterViewComponent.isParameterVisibleForUserType for the compare context
   * (isRemote = true, no disabled subsystems). When userOrFactory is 'customer', only
   * parameters visible to customers are considered; 'all' shows everything.
   */
  private isParameterVisibleForUserType(parameter: dsDtos.Parameter): boolean {
    const userOrFactory = this.userOrFactory();
    if (!userOrFactory || userOrFactory === 'all') return true;

    if (parameter.write !== true || parameter.obsolete) return false;
    if (parameter.isAvailableAsUserParameter !== true) return false;

    const writeAccessValues: dsEnums.UserAccess[] = ['WRITE_REMOTE'];
    const parameterUserAccess = this.deviceTypeParameterConfigWithAvailableList()
      .filter((c) => c.parameterId === parameter.id)
      .map((c) => c.userAccess);
    return parameterUserAccess.every((access) => writeAccessValues.includes(access));
  }

  /** Differing parameter IDs filtered to only those visible under the current userOrFactory toggle. */
  readonly visibleDifferingParameterIds = computed(() => {
    const diffIds = this.differingParameterIds();
    if (this.userOrFactory() === 'all') return diffIds;
    const result = new Set<string>();
    for (const parameter of this.availableParameters()) {
      if (diffIds.has(parameter.id) && this.isParameterVisibleForUserType(parameter)) {
        result.add(parameter.id);
      }
    }
    return result;
  });

  readonly valueOverrideByParameterId = computed<Map<string, Map<string, string>>>(() => {
    if (this.collars().length < 2) return new Map();
    const result = new Map<string, Map<string, string>>();
    for (const [key, value] of this.perCollarValues()) {
      result.set(`Device ${key}`, value);
    }
    return result;
  });

  readonly displayedParameters = computed(() => {
    const all = this.availableParameters();
    if (this.collars().length < 2 || this.showMode() === 'all') return all;
    const diffIds = this.differingParameterIds();
    return all.filter((p) => diffIds.has(p.id));
  });

  readonly hasNoDifferences = computed(() => {
    if (this.collars().length < 2) return false;
    if (this.showMode() !== 'differences') return false;
    return (
      this.visibleDifferingParameterIds().size === 0 &&
      this.multiDeviceValuesByScheduleType().size === 0
    );
  });

  /** Merges parameter data from all collars into ParameterDataService for form editing. */
  readonly syncParameterData = toObservable(this.collars)
    .pipe(takeUntilDestroyed())
    .subscribe((collars) => {
      const merged: dsDtos.ParameterData[] = [];
      const seen = new Set<string>();

      for (const collar of collars) {
        for (const pd of collar.parameterDataList) {
          const key = `${pd.parameterId}__${pd.isUserData}`;
          if (!seen.has(key)) {
            seen.add(key);
            merged.push(pd);
          }
        }
      }
      this.parameterDataService.init(merged);
    });
}
